import React, { useState } from 'react';
import classNames from 'classnames';
import styles from '../assets/grid.module.scss';
import { Paginated } from 'src/services/api-handlers/pagination';
import Grid, { Column } from 'src/components/react-table/Grid';
import textFilter from 'src/components/react-table/filters/text-filter';
import { QueryParams } from 'src/types/grid';
import { CellProps } from 'react-table';
import { Link } from 'react-router-dom';
import Router from 'src/navigation/router';
import { routes } from 'src/navigation';
import dropdownFilter from 'src/components/react-table/filters/dropdown-filter';

import { IProviderReport } from 'src/types/providerReport';
import user from '../../../../../../services/user-auth';
import UserRole from '../../../../../../enumerables/user-role';
import { numberWithCommas } from 'src/helpers/format';
import { IServiceType } from '../../../../../../types/service-types';
import { IServiceLocation } from '../../../../../../types/service-locations';

interface Props {
    serviceTypes?: Paginated<IServiceType>;
    serviceLocations?: Paginated<IServiceLocation>;
    data: Paginated<IProviderReport>;
    providers: SelectObject[];
    getData: (params: QueryParams) => void;
    getDataCsv: (params: QueryParams) => void;
}

const getColumns = (providers: SelectObject[]): Column<IProviderReport>[] => [
    {
        Header: 'Provider',
        accessor: 'id',
        disableSortBy: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IProviderReport>) => {
            if (original.totalRow) {
                return <b>Total</b>;
            }
            if (user.getRoles().includes(UserRole.ROLE_ADMIN)) {
                return (
                    <Link
                        to={Router.generate(routes.PROVIDER_DETAIL, { id: original.id, tab: 'main-info', subId: '' })}
                    >
                        {original.fullName ? original.fullName : null}
                    </Link>
                );
            } else {
                return original.fullName ? original.fullName : null;
            }
        },
        Filter: dropdownFilter<IProviderReport>(providers),
    },
    {
        Header: 'Worked,hrs',
        accessor: 'totalHoursWorked',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IProviderReport>) => {
            if (!original?.totalHoursWorked) return '';

            return original.totalHoursWorked ? `${numberWithCommas(original.totalHoursWorked)}` : null;
        },
        Filter: textFilter<IProviderReport>(),
    },
    {
        Header: 'Bonus,$',
        accessor: 'totalBonus',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IProviderReport>) => {
            if (!original?.totalBonus) return '';

            return original.totalBonus ? `$${numberWithCommas(original.totalBonus)}` : null;
        },
        Filter: textFilter<IProviderReport>(),
    },
    {
        Header: 'Admin Work,hrs',
        accessor: 'adminWorkTotal',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IProviderReport>) => {
            // if (!original?.fixedSalary) return '';
            if (original.totalRow) {
                return `${numberWithCommas(original.adminWorkTotal)}`;
            }
            return original.adminWorkTotal ? `${numberWithCommas(original.adminWorkTotal)}` : null;
        },
        Filter: textFilter<IProviderReport>(),
    },
    {
        Header: 'Internal,$',
        accessor: 'internalTotal',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IProviderReport>) => {
            // if (!original?.fixedSalary) return '';
            if (original.totalRow) {
                return `$${numberWithCommas(original.internalTotal)}`;
            }
            return original.internalTotal ? `$${numberWithCommas(original.internalTotal)}` : null;
        },
        Filter: textFilter<IProviderReport>(),
    },
    {
        Header: 'Payroll,$',
        accessor: 'totalPayroll',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IProviderReport>) => {
            if (!original?.totalPayroll) return '';

            return original.totalPayroll ? `$${numberWithCommas(original.totalPayroll)}` : null;
        },
        Filter: textFilter<IProviderReport>(),
    },
    {
        Header: (
            <span className={classNames(styles.relat)}>
                <span className={classNames(styles.title)}>
                    Confirmed,$ <div className={classNames(styles.hover)}>The total of confirmed sessions</div>
                </span>
            </span>
        ),
        accessor: 'totalConfirmedAmount',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IProviderReport>) => {
            if (!original?.totalConfirmedAmount) return '';

            return original.totalConfirmedAmount ? `$${numberWithCommas(original.totalConfirmedAmount)}` : null;
        },
        Filter: textFilter<IProviderReport>(),
    },
    {
        Header: (
            <span className={classNames(styles.relat)}>
                <span className={classNames(styles.title)}>
                    Invoice Amount,$ <div className={classNames(styles.hover)}>The total of invoices</div>
                </span>
            </span>
        ),
        accessor: 'totalBilledAmount',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IProviderReport>) => {
            if (!original?.totalBilledAmount) return '';

            return original.totalBilledAmount ? `$${numberWithCommas(original.totalBilledAmount)}` : null;
        },
        Filter: textFilter<IProviderReport>(),
    },
    {
        Header: (
            <span className={classNames(styles.relat)}>
                <span className={classNames(styles.title)}>
                    Invoice Paid,$ <div className={classNames(styles.hover)}>The paid amount of invoices</div>
                </span>
            </span>
        ),
        accessor: 'totalBilledPaidAmount',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IProviderReport>) => {
            if (!original?.totalBilledPaidAmount) return '';

            return original.totalBilledPaidAmount ? `$${numberWithCommas(original.totalBilledPaidAmount)}` : null;
        },
        Filter: textFilter<IProviderReport>(),
    },
    {
        Header: (
            <span className={classNames(styles.relat)}>
                <span className={classNames(styles.title)}>
                    Invoice Due,${' '}
                    <div className={classNames(styles.hover)}>
                        Invoices with the status "Sent" + "Outstanding payment" for the selected period
                    </div>
                </span>
            </span>
        ),
        accessor: 'totalBilledDueAmount',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IProviderReport>) => {
            if (!original?.totalBilledDueAmount) return '';

            return original.totalBilledDueAmount ? `$${numberWithCommas(original.totalBilledDueAmount)}` : null;
        },
        Filter: textFilter<IProviderReport>(),
    },
    {
        Header: (
            <span className={classNames(styles.relat)}>
                <span className={classNames(styles.title)}>
                    Invoice Written-off,${' '}
                    <div className={classNames(styles.hover)}>The total amount of written-off invoices</div>
                </span>
            </span>
        ),
        accessor: 'totalWrittenOffAmount',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IProviderReport>) => {
            if (!original?.totalWrittenOffAmount) return '';

            return original.totalWrittenOffAmount ? `$${numberWithCommas(original.totalWrittenOffAmount)}` : null;
        },
        Filter: textFilter<IProviderReport>(),
    },
    {
        Header: (
            <span className={classNames(styles.relat)}>
                <span className={classNames(styles.title)}>
                    Owed to staff,$ <div className={classNames(styles.hover)}>The amount should be paid to staff</div>
                </span>
            </span>
        ),
        accessor: 'totalPayrollLeft',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IProviderReport>) => {
            if (!original?.totalPayrollLeft) return '';

            return original.totalPayrollLeft ? `$${numberWithCommas(original.totalPayrollLeft)}` : null;
        },
        Filter: textFilter<IProviderReport>(),
    },
    {
        Header: 'Fixed Salary,$',
        accessor: 'salaryTotal',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IProviderReport>) => {
            // if (!original?.fixedSalary) return '';
            if (original.totalRow) {
                return `$${numberWithCommas(original.salaryTotal)}`;
            }
            return (
                <Link to={Router.generate(routes.WAGES_LIST, { param: original.id })}>
                    {/*{original.fixedSalary ? original.fixedSalary : null}*/}
                    {original.salaryTotal ? `$${numberWithCommas(original.salaryTotal)}` : null}
                </Link>
            );
        },
        Filter: textFilter<IProviderReport>(),
    },
    {
        Header: (
            <span className={classNames(styles.relat)}>
                <span className={classNames(styles.title)}>
                    {' '}
                    Total Collected,$ <div className={classNames(styles.hover)}> The total of paid invoices</div>
                </span>
            </span>
        ),
        accessor: 'totalCollected',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IProviderReport>) => {
            if (!original?.totalCollected) return '';

            return original.totalCollected ? `$${numberWithCommas(original.totalCollected)}` : null;
        },
        Filter: textFilter<IProviderReport>(),
    },
];

const ProviderReportsGrid: React.FunctionComponent<Props> = ({
    data,
    getData,
    providers,
    getDataCsv,
    serviceTypes,
    serviceLocations,
}: Props) => {
    const [columns, setColumns] = useState<any>([]);

    if (providers.length && !columns.length) {
        setColumns(getColumns(providers));
    }

    return (
        <div className={classNames(styles.grid)}>
            <Grid<IProviderReport>
                getDataCsv={getDataCsv}
                filterGlobal={true}
                columns={columns}
                data={data}
                serviceTypes={serviceTypes}
                serviceLocations={serviceLocations}
                getData={getData}
            />
        </div>
    );
};

export default ProviderReportsGrid;
